import { DocumentStatus } from '@kanbu/schema/enums';
import { useCallback, useEffect, useMemo, useRef } from 'preact/compat';

import { HowItWorksSection } from '@/components/how-it-works-section/how-it-works-section';
import { InfoSection } from '@/components/info-section/info-section';
import { KnownSourcesSection } from '@/components/known-sources-section/known-sources-section';
import { ShareLinkSection } from '@/components/share-link-section/share-link-section';
import { SuccessBanner } from '@/components/success-banner/success-banner';
import { AppSettings } from '@/constants/app-settings';
import { useChatbotLoader } from '@/hooks/use-chatbot-loader';
import { useFormElements } from '@/hooks/use-form-elements';
import { useUrlSubmission } from '@/hooks/use-url-submission';

export function App() {
  const { isLoading, organizationId, chatId, urlStatusUpdates, submitUrl } =
    useUrlSubmission();

  useFormElements(submitUrl, isLoading);

  const chatbotRef = useChatbotLoader(chatId);
  const hasOpenedChatbotRef = useRef(false);

  const isDone = !!(!isLoading && organizationId && chatId);
  const showInfoSection = isLoading || urlStatusUpdates.length === 0;

  const filteredUrlStatusUpdates = useMemo(() => {
    const uniqueUpdates = new Map(
      urlStatusUpdates.map(update => [update.id, update]),
    );

    return Array.from(uniqueUpdates.values());
  }, [urlStatusUpdates]);

  useEffect(() => {
    if (urlStatusUpdates.length === 0) {
      hasOpenedChatbotRef.current = false;
    }
  }, [urlStatusUpdates.length]);

  useEffect(() => {
    if (
      !hasOpenedChatbotRef.current &&
      chatbotRef.current &&
      filteredUrlStatusUpdates.some(
        update => update.status === DocumentStatus.Done,
      )
    ) {
      chatbotRef.current.open();
      hasOpenedChatbotRef.current = true;
    }
  }, [filteredUrlStatusUpdates, chatbotRef]);

  const playgroundUrl = useMemo(() => {
    if (!chatId) {
      return '';
    }

    return `${AppSettings.playgroundUrl}/${chatId}/campaign?utm_source=kanbu.ai&kanbu-campaign-email=`;
  }, [chatId]);

  const onOpenChat = useCallback(() => {
    chatbotRef.current?.open();
  }, [chatbotRef]);

  return (
    <div className='kanbu-web-input'>
      <div className='relative flex flex-col gap-4'>
        {isDone && <SuccessBanner onOpenChat={onOpenChat} />}
        {showInfoSection && (
          <InfoSection
            isLoading={isLoading}
            urlStatusUpdates={filteredUrlStatusUpdates}
          />
        )}
        {isDone && (
          <>
            <ShareLinkSection playgroundUrl={playgroundUrl} />
            <KnownSourcesSection urlStatusUpdates={filteredUrlStatusUpdates} />
            <HowItWorksSection organizationId={organizationId} />
          </>
        )}
      </div>
    </div>
  );
}
